/**
 * Impersonation header attach helper — web platform (spec 048, FR-014 / FR-044).
 *
 * Single source of truth for when `X-Morfi-Impersonate-Grant` is appended
 * to outbound requests. The header is attached iff:
 *   1. Impersonation is active in this tab (signalled by `grantToken` being non-null).
 *   2. The request URL starts with the `expectedApiOrigin` (so a devnet
 *      grant cannot leak onto a mainnet API call or any third-party URL).
 *
 * `expectedApiOrigin` should be the resolved API base URL for the active
 * network (e.g. `https://api.morfi.markets`). In dev (relative URLs) pass
 * an empty string to skip the origin check and attach to all relative fetches.
 *
 * The store state is read via the `getImpersonationState` getter to avoid
 * a hard Zustand dependency in the SDK package.
 *
 * Relocated from `frontend/src/lib/impersonation/header.ts` as part of
 * the auth cutover (spec 009, FILE 14, Group F).
 */

export const IMPERSONATION_HEADER_NAME = 'X-Morfi-Impersonate-Grant';

export interface ImpersonationStateSnapshot {
  isActive: boolean;
  grantToken: string | null;
  network: string | null;
}

/**
 * Returns a new `RequestInit` with the impersonation header attached,
 * or `init` unchanged when impersonation is not active or the URL does
 * not match the expected API origin.
 *
 * @param url - The full or relative URL of the fetch request.
 * @param init - The existing `RequestInit`, if any.
 * @param state - Current impersonation state snapshot from the store.
 * @param expectedApiOrigin - The API base URL to restrict header injection to.
 *   Pass `''` to attach to all URLs (dev with relative paths).
 */
export function attachImpersonationHeader(
  url: string,
  init: RequestInit | undefined,
  state: ImpersonationStateSnapshot,
  expectedApiOrigin: string,
): RequestInit {
  if (!state.isActive || state.grantToken === null || state.network === null) {
    return init ?? {};
  }

  if (expectedApiOrigin.length > 0 && !url.startsWith(expectedApiOrigin)) {
    return init ?? {};
  }

  const headers = new Headers(init?.headers);
  headers.set(IMPERSONATION_HEADER_NAME, state.grantToken);
  return { ...init, headers };
}
